import { Box, Dialog, DialogActions, DialogContent, Typography, useTheme } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertComponent, Button, SplashScreen, DayScheduleEditor, IDaySchedule, createDefaultWeekSchedule, scheduleToApiDto, weeklySchedulesToDays } from '../../shared/components';
import { LOCAL_STORAGE_KEY_USER_ROLES, LOCAL_STORAGE_KEY_USER_EMAIL_CONFIRMED, useUserContext } from '../../shared/contexts';
import { businessEmployeeService, employeeService, authService, feedback } from '../../shared/services';
import { Api } from '../../shared/services/api/axios-config';
import { setCsrfRequestToken } from '../../shared/services/api/axios-config/interceptors';

export const CreateProfessional: React.FC = () => {
  const [schedule, setSchedule] = useState<IDaySchedule[]>(() => createDefaultWeekSchedule());
  const [isFinishing, setIsFinishing] = useState(false);
  const [showEmailMismatchWarning, setShowEmailMismatchWarning] = useState(false);

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { setUserRoles, setEmailConfirmed } = useUserContext();
  const theme = useTheme();
  const navigate = useNavigate();

  // 1. Get invite info from token
  const { data: inviteInfo, isLoading: isLoadingInvite, isError: isInviteError } = useQuery({
    queryKey: ['invite-info', token],
    queryFn: () => employeeService.getInviteInfo(token!),
    enabled: !!token,
  });

  // 2. Try to get user from cookie
  const { data: user, isLoading: isLoadingUser } = useQuery(
    ['user'],
    () => authService.getUserFromCookie(),
    { retry: false }
  );

  // 1b. Sync schedule with business default schedules when inviteInfo loads
  useEffect(() => {
    if (inviteInfo?.weeklySchedules) {
      setSchedule(weeklySchedulesToDays(inviteInfo.weeklySchedules));
    }
  }, [inviteInfo?.weeklySchedules]);

  // 1c. Warn if logged-in email differs from invited email
  useEffect(() => {
    if (inviteInfo && user && inviteInfo.email !== user.email) {
      setShowEmailMismatchWarning(true);
    }
  }, [inviteInfo, user]);

  // 3. Check if already a business employee
  const { data: businessEmployee } = useQuery({
    queryKey: ['businessEmployee', user?.id, inviteInfo?.businessId],
    queryFn: () => {
      if (user && inviteInfo) {
        return businessEmployeeService.getByUserIdAndBusinessId(user.id, inviteInfo.businessId);
      }
    },
    enabled: !!user && !!inviteInfo,
  });

  // 4. Accept invite mutation
  const { mutate: acceptInvite, isLoading: isLoadingAccept } = useMutation(
    () => {
      if (!inviteInfo || !token) throw new Error('Dados incompletos');

      return employeeService.acceptInvite({
        token,
        weeklySchedules: scheduleToApiDto(schedule),
        color: theme.palette.primary.main,
        servicesCommissionRate: 10,
        isReceptionist: inviteInfo.isReceptionist,
      });
    },
    {
      onSuccess: (userView) => {
        setIsFinishing(true);
        feedback('Informações salvas!', 'success');

        // Update user roles from the response (backend already set the cookie)
        const employeeRoles = userView.userRoles;
        setUserRoles(employeeRoles);
        localStorage.setItem(LOCAL_STORAGE_KEY_USER_ROLES, JSON.stringify(employeeRoles));

        // Email já foi confirmado pelo backend — atualizar estado + localStorage
        if (userView.emailConfirmed) {
          setEmailConfirmed(true);
          localStorage.setItem(LOCAL_STORAGE_KEY_USER_EMAIL_CONFIRMED, JSON.stringify(true));
        }

        navigate('/home');
      },
      onError: (error: Error) => {
        feedback(error.message, 'error');
      },
    }
  );

  const handleAcceptInvite = useCallback(() => {
    // Fetch fresh CSRF token before submitting (token may be stale after login)
    Api.get('/auth/antiforgery-token').then((res) => {
      if (res.data) setCsrfRequestToken(res.data as string);
    }).catch(() => undefined).finally(() => {
      acceptInvite();
    });
  }, [acceptInvite]);

  // --- Renderização ---

  // Loading state
  if (isLoadingInvite || isLoadingAccept || isLoadingUser || isFinishing) {
    return (
      <>
        <AlertComponent />
        <SplashScreen />
      </>
    );
  }

  // Token error
  if (isInviteError || !inviteInfo) {
    return (
      <>
        <AlertComponent />
        <Dialog open>
          <DialogContent>
            <Typography color='error'>
              Link de convite inválido ou expirado. Solicite um novo convite ao administrador.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Box display='flex' justifyContent='center' flex={1}>
              <Button borderRounded variant='contained' label='Voltar' onClick={() => navigate('/')} />
            </Box>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Already a professional for this business
  if (businessEmployee) {
    return (
      <>
        <AlertComponent />
        <Dialog open>
          <DialogContent>
            <Typography>Ops! Parece que você já é profissional deste estabelecimento.</Typography>
          </DialogContent>
          <DialogActions>
            <Box display='flex' justifyContent='center' flex={1}>
              <Button borderRounded variant='contained' label='Página inicial' onClick={() => navigate('/home')} />
            </Box>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // --- Email mismatch warning ---
  if (showEmailMismatchWarning) {
    return (
      <>
        <AlertComponent />
        <Dialog open>
          <DialogContent>
            <Typography variant='h6' gutterBottom>⚠️ Atenção: e-mail diferente</Typography>
            <Typography>
              Este convite foi enviado para <strong>{inviteInfo?.email}</strong>, mas você está logado como <strong>{user?.email}</strong>.
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
              Pode continuar mesmo assim — o convite será vinculado à sua conta atual. Se achar que isso é um erro, cancele e entre em contato com o administrador.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Box display='flex' justifyContent='center' flex={1} gap={1}>
              <Button borderRounded variant='outlined' label='Cancelar' onClick={() => navigate('/home')} />
              <Button borderRounded variant='contained' label='Continuar mesmo assim' onClick={() => setShowEmailMismatchWarning(false)} />
            </Box>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // --- Horários de trabalho ---
  return (
    <>
      <AlertComponent />
      <Dialog open>
        <DialogContent>
          <Box display='flex' alignItems='center' flexDirection='column' gap={2}>
            <Typography>
              Deseja se cadastrar como {inviteInfo.isReceptionist ? 'recepcionista' : 'profissional'} do estabelecimento {inviteInfo.businessName}?
            </Typography>

            <Typography variant='body2'>
              Para confirmar, digite seu horário de trabalho. (Pode ser alterado no futuro)
            </Typography>

            <DayScheduleEditor
              schedule={schedule}
              onChange={setSchedule}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Box display='flex' justifyContent='center' flex={1}>
            <Button
              borderRounded
              variant='contained'
              label='Confirmar'
              onClick={handleAcceptInvite}
              isLoading={isLoadingAccept}
            />
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};
